import { type RefObject, useEffect } from 'react';
import { appContext } from '../../app/context';
import { applyStrokeToLayer, ensurePaintTarget, type PaintTarget } from '../../app/paintTarget';
import { layerEditStale, STALE_EDIT_MESSAGE } from '../../app/staleGuard';
import { hexToRgba, toleranceToSquared } from '../../core/paint/colour';
import {
  isNegligibleDrag,
  type ShapeGeometry,
  type ShapeKind,
  shapeFromDrag,
} from '../../core/paint/shapes';
import { fillLayer, sampledColour } from '../../io/floodFill';
import { bakeShape, drawShape } from '../../io/paintShape';
import { useEditorStore } from '../../state/editorStore';
import { clientToDoc } from '../pointer';

/**
 * The point-and-drag paint tools: eyedropper, flood fill, and the shapes.
 *
 * They share a shape with the brush: act on a layer's PNG, commit exactly once
 * per user action, and refuse out loud when there is nothing to act on.
 */

const SHAPE_TOOLS: Record<string, ShapeKind> = {
  line: 'line',
  rect: 'rect',
  ellipse: 'ellipse',
  arrow: 'arrow',
};

interface DragSession {
  target: PaintTarget;
  shape: ShapeGeometry;
  element: HTMLCanvasElement;
}

export function useShapeTools(
  stageRef: RefObject<HTMLDivElement | null>,
  previewHostRef: RefObject<HTMLDivElement | null>,
): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let session: DragSession | null = null;

    const layerPoint = (event: PointerEvent, target: PaintTarget) => {
      const viewport = useEditorStore.getState().viewport;
      const doc = clientToDoc(event.clientX, event.clientY, stage, viewport);
      return {
        x: (doc.x - target.x) / target.scaleX,
        y: (doc.y - target.y) / target.scaleY,
      };
    };

    const style = () => {
      const { color, paint, shape } = useEditorStore.getState();
      return { color, opacity: paint.opacity, filled: shape.filled };
    };

    const positionPreview = (element: HTMLCanvasElement, target: PaintTarget) => {
      const { viewport } = useEditorStore.getState();
      element.style.position = 'absolute';
      element.style.left = `${target.x * viewport.zoom + viewport.pan.x}px`;
      element.style.top = `${target.y * viewport.zoom + viewport.pan.y}px`;
      element.style.width = `${target.width * target.scaleX * viewport.zoom}px`;
      element.style.height = `${target.height * target.scaleY * viewport.zoom}px`;
      element.style.pointerEvents = 'none';
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const tool = useEditorStore.getState().activeTool;
      const isShape = tool in SHAPE_TOOLS;
      if (tool !== 'eyedropper' && tool !== 'fill' && !isShape) return;
      // A second pointer (pen + touch, palm contact) must not orphan the drag
      // already in flight — its preview would leak and its points would be lost.
      if (session) return;

      const viewport = useEditorStore.getState().viewport;
      const docPoint = clientToDoc(event.clientX, event.clientY, stage, viewport);

      if (tool === 'eyedropper') {
        const colour = sampledColour(appContext.getEngine()?.sampleDocument(docPoint) ?? null);
        if (colour) {
          useEditorStore.getState().setColor(colour);
          appContext.notify(`Picked ${colour}`);
        } else {
          appContext.notify('Nothing to pick there — that pixel is empty.', 'warn');
        }
        event.preventDefault();
        return;
      }

      const target = ensurePaintTarget(appContext);
      if (!target) return;

      const focused = document.activeElement;
      if (focused instanceof HTMLElement && focused.tagName === 'INPUT') focused.blur();

      if (tool === 'fill') {
        event.preventDefault();
        const point = {
          x: (docPoint.x - target.x) / target.scaleX,
          y: (docPoint.y - target.y) / target.scaleY,
        };
        const { color, fillTolerance } = useEditorStore.getState();
        void fillLayer({
          src: target.src,
          width: target.width,
          height: target.height,
          x: point.x,
          y: point.y,
          colour: hexToRgba(color),
          toleranceSquared: toleranceToSquared(fillTolerance),
        })
          .then((result) => {
            if ('reason' in result) {
              appContext.notify(
                result.reason === 'outside'
                  ? 'Click inside the canvas to fill.'
                  : 'That area is already that colour.',
                'warn',
              );
              return;
            }
            const store = useEditorStore.getState();
            if (layerEditStale(appContext, target)) {
              appContext.notify(STALE_EDIT_MESSAGE, 'warn');
              return;
            }
            store.commit('Fill', applyStrokeToLayer(store.doc, target, result.pixels));
          })
          .catch((error: unknown) =>
            appContext.notify(error instanceof Error ? error.message : 'Fill failed.', 'error'),
          );
        return;
      }

      if (!isShape) return;
      event.preventDefault();
      const from = {
        x: (docPoint.x - target.x) / target.scaleX,
        y: (docPoint.y - target.y) / target.scaleY,
      };
      const element = document.createElement('canvas');
      const kind = SHAPE_TOOLS[tool];
      if (!kind) return;
      element.width = Math.max(1, Math.round(target.width));
      element.height = Math.max(1, Math.round(target.height));
      element.dataset.testid = 'shape-preview';
      positionPreview(element, target);
      previewHostRef.current?.appendChild(element);

      session = {
        target,
        shape: shapeFromDrag(kind, from, from, useEditorStore.getState().shape.width),
        element,
      };
    };

    const redraw = () => {
      if (!session) return;
      const ctx = session.element.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, session.element.width, session.element.height);
      const { color, opacity, filled } = style();
      drawShape(ctx, session.shape, { color, opacity, filled });
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!session) return;
      const kind = SHAPE_TOOLS[useEditorStore.getState().activeTool];
      if (!kind) return;
      session.shape = shapeFromDrag(
        kind,
        session.shape.from,
        layerPoint(event, session.target),
        useEditorStore.getState().shape.width,
      );
      redraw();
    };

    const finish = async () => {
      if (!session) return;
      const current = session;
      session = null;

      try {
        if (isNegligibleDrag(current.shape.from, current.shape.to)) return;
        const { color, opacity, filled } = style();
        const pixels = await bakeShape({
          src: current.target.src,
          width: current.target.width,
          height: current.target.height,
          shape: current.shape,
          style: { color, opacity, filled },
        });
        const store = useEditorStore.getState();
        const label = `${current.shape.kind[0]?.toUpperCase()}${current.shape.kind.slice(1)}`;
        // Same window as a stroke: the shape baked from the pointerdown layer.
        if (layerEditStale(appContext, current.target)) {
          appContext.notify(STALE_EDIT_MESSAGE, 'warn');
          return;
        }
        store.commit(label, applyStrokeToLayer(store.doc, current.target, pixels));
      } catch (error) {
        appContext.notify(
          error instanceof Error ? error.message : 'That shape could not be drawn.',
          'error',
        );
      } finally {
        current.element.remove();
      }
    };

    const onPointerUp = () => void finish();
    const onPointerCancel = () => {
      // A cancelled gesture (touch scroll takeover, palm rejection) commits
      // nothing and leaves nothing behind.
      if (!session) return;
      session.element.remove();
      session = null;
    };

    stage.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      session?.element.remove();
      session = null;
    };
  }, [stageRef, previewHostRef]);
}
